import { z } from 'zod';

/**
 * String enums mirrored 1:1 from apps/api/prisma/schema.prisma (and engine types).
 * FROZEN with the schema. A test in apps/api asserts these match the Prisma enums.
 */
export const TenantKind = z.enum(['GOLF_COURSE', 'ORGANIZER', 'SOCIETY', 'PERSONAL']);
export const TenantStatus = z.enum(['ACTIVE', 'SUSPENDED', 'ARCHIVED']);
export const UserStatus = z.enum(['ACTIVE', 'INVITED', 'DISABLED']);
export const TenantRole = z.enum(['TENANT_ADMIN', 'TENANT_STAFF']);
export const TournamentRole = z.enum([
  'TOURNAMENT_ADMIN',
  'TOURNAMENT_OPERATOR',
  'TOURNAMENT_VIEWER',
]);
export const ClubRole = z.enum(['CLUB_ADMIN', 'CLUB_MEMBER']);
export const MembershipStatus = z.enum(['ACTIVE', 'PENDING', 'LEFT', 'REMOVED']);
export const SessionKind = z.enum(['USER', 'CADDY', 'PLAYER']);
export const SessionScope = z.enum(['PLAYER_VIEW', 'SELF_ENTRY']);
export const CourseVisibility = z.enum(['PRIVATE', 'SHARED']);
export const Gender = z.enum(['M', 'F']);
export const TournamentKind = z.enum(['TOURNAMENT', 'CLUB_EVENT']);
export const TournamentStatus = z.enum([
  'DRAFT',
  'REGISTRATION_OPEN',
  'REGISTRATION_CLOSED',
  'IN_PROGRESS',
  'COMPLETED',
  'FINALIZED',
  'CANCELLED',
]);
export const RoundStatus = z.enum(['SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'LOCKED']);
export const StartType = z.enum(['SINGLE_TEE', 'TWO_TEE', 'SHOTGUN']);
export const FormatKind = z.enum([
  'STROKE_GROSS',
  'STROKE_NET',
  'STABLEFORD',
  'SCRAMBLE',
  'MATCH_SINGLE',
  'MATCH_FOURSOME',
  'MATCH_FOURBALL',
]);
export const ViewKind = z.enum(['STROKE_GROSS', 'STROKE_NET', 'STABLEFORD']);
export const ParticipantStatus = z.enum([
  'PENDING',
  'APPROVED',
  'WAITLISTED',
  'REJECTED',
  'WITHDRAWN',
]);
export const PlayStatus = z.enum(['ACTIVE', 'WD', 'DQ', 'DNS', 'CUT']);
export const RegistrationSource = z.enum(['WEB_FORM', 'IMPORT', 'ADMIN', 'CLUB']);
export const CategoryFieldType = z.enum(['SELECT', 'TEXT', 'NUMBER']);
export const CategoryDerivation = z.enum([
  'NONE',
  'GENDER',
  'AGE',
  'HANDICAP',
  'NATIONALITY',
  'PRO_AM',
]);
export const ContestType = z.enum(['NEAREST_PIN', 'LONGEST_DRIVE']);
export const EntryStatus = z.enum(['VALID', 'VOIDED', 'SUPERSEDED']);
export const AwardType = z.enum([
  'HOLE_IN_ONE',
  'ALBATROSS',
  'EAGLE',
  'BIRDIE',
  'MOST_BIRDIES',
  'CUSTOM',
]);
export const LinkPurpose = z.enum(['PUBLIC_VIEW', 'CADDY_PORTAL', 'ADMIN_CONSOLE', 'REGISTRATION']);
export const ActorType = z.enum(['CADDY', 'USER', 'PLAYER', 'SYSTEM']);
export const ScoreSource = z.enum(['CADDY', 'ADMIN', 'SELF', 'IMPORT']);
export const ScoreAuditAction = z.enum([
  'CREATE',
  'UPDATE',
  'CLEAR',
  'ADMIN_CORRECTION',
  'REJECTED',
]);
export const SignOffStatus = z.enum(['SIGNED', 'DISPUTED', 'INVALIDATED']);
export const SignOffMethod = z.enum(['PLAYER_PORTAL', 'CADDY_DEVICE', 'ADMIN']);
export const AnomalyKind = z.enum([
  'HIGH_EDIT_RATE_CADDY',
  'HIGH_EDIT_RATE_PARTICIPANT',
  'IMPROBABLE_SCORE',
  'LATE_EDIT',
  'OUT_OF_SEQUENCE_ENTRY',
  'BURST_ENTRY',
  'EDIT_AFTER_SIGNOFF',
  'REJECTED_ATTEMPTS',
  'CONTEST_OUTLIER',
  'CONTEST_EDIT',
  'DEVICE_SWITCH',
  'OFFLINE_BACKDATED',
  'CADDY_LOGIN_FAILURES',
]);
export const AnomalySeverity = z.enum(['INFO', 'WARNING', 'CRITICAL']);
export const AnomalyStatus = z.enum(['OPEN', 'ACKNOWLEDGED', 'DISMISSED', 'RESOLVED']);
export const PricingMode = z.enum(['VOLUME', 'GRADUATED', 'FLAT']);
export const ChargeStatus = z.enum(['ESTIMATE', 'FINALIZED', 'INVOICED', 'VOID']);
export const StatementStatus = z.enum(['DRAFT', 'ISSUED', 'PAID', 'VOID']);
export const PaymentStatus = z.enum(['PENDING', 'SUCCEEDED', 'FAILED', 'REFUNDED']);
export const ImportStatus = z.enum(['PREVIEW', 'COMMITTED', 'DISCARDED', 'FAILED']);
export const MatchStatus = z.enum(['SCHEDULED', 'IN_PROGRESS', 'COMPLETE']);
export const MatchSideKey = z.enum(['A', 'B']);
export const BannerPlacement = z.enum([
  'LEADERBOARD_TOP',
  'LEADERBOARD_INLINE',
  'TV_ROTATION',
  'FOOTER',
  'CADDY_APP',
]);
export const HandicapRecordSource = z.enum(['TOURNAMENT', 'MANUAL', 'IMPORT']);
export const ResultSnapshotKind = z.enum(['PROVISIONAL', 'FINAL']);
/** mobile app (CLAUDE.md §4.9) */
export const MobilePlatform = z.enum(['ANDROID', 'IOS']);
export const PushKind = z.enum([
  'ROUND_STARTED',
  'SIGNOFF_REQUESTED',
  'RESULTS_FINALIZED',
  'TEE_TIME_REMINDER',
]);
export const PushStatus = z.enum([
  'PENDING',
  'SENDING',
  'SENT',
  'DELIVERED',
  'FAILED',
  'UNDELIVERABLE',
  'SKIPPED',
]);
/** 나의 대회 (CLAUDE.md §4.10, §15 #405–#416) */
export const PersonalGrantSource = z.enum([
  'SUBSCRIPTION',
  'COUPON',
  'ONE_TIME',
  'MANUAL',
  /** launch free period activation (§15 #434) */
  'FREE_PROMO',
]);
export const PersonalOrderKind = z.enum(['SUBSCRIPTION', 'ONE_TIME']);
export const PersonalOrderStatus = z.enum([
  'PENDING',
  'APPROVED',
  'REJECTED',
  'CANCELLED',
  /** store refund / chargeback (Google Play voided purchase) */
  'REFUNDED',
]);
/** Google Play Billing (CLAUDE.md §15 #428–#437) */
export const PlayProductKind = z.enum(['CREDIT', 'SUBSCRIPTION']);
export const PlayPurchaseState = z.enum([
  'PENDING',
  'PURCHASED',
  'ACTIVE',
  'IN_GRACE_PERIOD',
  'ON_HOLD',
  'PAUSED',
  'CANCELED',
  'EXPIRED',
  'REVOKED',
  'REFUNDED',
]);
export const PersonalMemberSource = z.enum(['MANUAL', 'INVITE', 'CLUB_IMPORT']);
/** sign-in methods besides e-mail + password; FIREBASE_* = one Firebase project (Google, phone) — §15 #417 */
export const AuthProvider = z.enum(['KAKAO', 'ZALO', 'FIREBASE_GOOGLE', 'FIREBASE_PHONE']);

export type TenantKind = z.infer<typeof TenantKind>;
export type PersonalGrantSource = z.infer<typeof PersonalGrantSource>;
export type PersonalOrderKind = z.infer<typeof PersonalOrderKind>;
export type PersonalOrderStatus = z.infer<typeof PersonalOrderStatus>;
export type PersonalMemberSource = z.infer<typeof PersonalMemberSource>;
export type PlayProductKind = z.infer<typeof PlayProductKind>;
export type PlayPurchaseState = z.infer<typeof PlayPurchaseState>;
export type AuthProvider = z.infer<typeof AuthProvider>;
export type TenantRole = z.infer<typeof TenantRole>;
export type TournamentRole = z.infer<typeof TournamentRole>;
export type ClubRole = z.infer<typeof ClubRole>;
export type SessionKind = z.infer<typeof SessionKind>;
export type SessionScope = z.infer<typeof SessionScope>;
export type Gender = z.infer<typeof Gender>;
export type TournamentStatus = z.infer<typeof TournamentStatus>;
export type RoundStatus = z.infer<typeof RoundStatus>;
export type StartType = z.infer<typeof StartType>;
export type FormatKind = z.infer<typeof FormatKind>;
export type ViewKind = z.infer<typeof ViewKind>;
export type ParticipantStatus = z.infer<typeof ParticipantStatus>;
export type PlayStatus = z.infer<typeof PlayStatus>;
export type ContestType = z.infer<typeof ContestType>;
export type AwardType = z.infer<typeof AwardType>;
export type LinkPurpose = z.infer<typeof LinkPurpose>;
export type ActorType = z.infer<typeof ActorType>;
export type ScoreAuditAction = z.infer<typeof ScoreAuditAction>;
export type SignOffStatus = z.infer<typeof SignOffStatus>;
export type SignOffMethod = z.infer<typeof SignOffMethod>;
export type EntryStatus = z.infer<typeof EntryStatus>;
export type CategoryDerivation = z.infer<typeof CategoryDerivation>;
export type AnomalyKind = z.infer<typeof AnomalyKind>;
export type AnomalySeverity = z.infer<typeof AnomalySeverity>;
export type AnomalyStatus = z.infer<typeof AnomalyStatus>;
export type PricingMode = z.infer<typeof PricingMode>;
export type BannerPlacement = z.infer<typeof BannerPlacement>;
export type MatchSideKey = z.infer<typeof MatchSideKey>;
export type MobilePlatform = z.infer<typeof MobilePlatform>;
export type PushKind = z.infer<typeof PushKind>;
export type PushStatus = z.infer<typeof PushStatus>;

/** All enum schemas by Prisma enum name (used by the api parity test). */
export const ALL_ENUMS = {
  TenantKind,
  TenantStatus,
  UserStatus,
  TenantRole,
  TournamentRole,
  ClubRole,
  MembershipStatus,
  SessionKind,
  SessionScope,
  CourseVisibility,
  Gender,
  TournamentKind,
  TournamentStatus,
  RoundStatus,
  StartType,
  FormatKind,
  ParticipantStatus,
  PlayStatus,
  RegistrationSource,
  CategoryFieldType,
  CategoryDerivation,
  ContestType,
  EntryStatus,
  AwardType,
  LinkPurpose,
  ActorType,
  ScoreSource,
  ScoreAuditAction,
  SignOffStatus,
  SignOffMethod,
  AnomalyKind,
  AnomalySeverity,
  AnomalyStatus,
  PricingMode,
  ChargeStatus,
  StatementStatus,
  PaymentStatus,
  ImportStatus,
  MatchStatus,
  MatchSideKey,
  BannerPlacement,
  HandicapRecordSource,
  ResultSnapshotKind,
  MobilePlatform,
  PushKind,
  PushStatus,
  PersonalGrantSource,
  PersonalOrderKind,
  PersonalOrderStatus,
  PersonalMemberSource,
  AuthProvider,
  PlayProductKind,
  PlayPurchaseState,
} as const;
